import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  timestamp: Date;
  read: boolean;
}

export function NotificationDropdown() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isNearEdge, setIsNearEdge] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ right?: number; left?: number } | undefined>(undefined);
  const [notifications, setNotifications] = useState<Notification[]>([
    {
      id: '1',
      title: 'System Update',
      message: 'The system will be updated tonight at 2 AM UTC.',
      type: 'info',
      timestamp: new Date(Date.now() - 1000 * 60 * 30),
      read: false,
    },
    {
      id: '2',
      title: 'Agent Offline',
      message: 'Agent "Sales Assistant" has been offline for 15 minutes.',
      type: 'warning',
      timestamp: new Date(Date.now() - 1000 * 60 * 60),
      read: false,
    },
    {
      id: '3',
      title: 'High Call Volume',
      message: 'Current call volume exceeds normal levels.',
      type: 'info',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
      read: true,
    },
  ]);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Calculate dropdown position based on viewport and direction
  useEffect(() => {
    if (!isOpen) {
      setIsNearEdge(false);
      setDropdownPosition(undefined);
      return;
    }

    const calculatePosition = () => {
      const button = buttonRef.current;
      if (!button) return;

      const buttonRect = button.getBoundingClientRect();
      const dropdownWidth = 360;
      const edgePadding = 16;
      const direction = document.documentElement.getAttribute('dir') || 'ltr';
      const isRTL = direction === 'rtl';

      let nearEdge = false;
      let positionStyle: { right?: number; left?: number } | undefined;

      if (isRTL) {
        // RTL: Default aligns to left of button (dropdown extends right)
        // Check if dropdown would overflow right edge of viewport
        nearEdge = buttonRect.left + dropdownWidth > window.innerWidth - edgePadding;

        if (nearEdge) {
          // Flip to right side of button (dropdown extends left)
          positionStyle = { right: window.innerWidth - buttonRect.right };
        } else {
          positionStyle = { left: buttonRect.left };
        }
      } else {
        // LTR: Default aligns to right of button (dropdown extends left)
        // Check if dropdown would overflow left edge of viewport
        nearEdge = buttonRect.right - dropdownWidth < edgePadding;

        if (nearEdge) {
          // Flip to left side of button (dropdown extends right)
          positionStyle = { left: buttonRect.right - dropdownWidth };
        } else {
          positionStyle = { right: window.innerWidth - buttonRect.right };
        }
      }

      setIsNearEdge(nearEdge);
      setDropdownPosition(positionStyle);
    };

    calculatePosition();
    window.addEventListener('resize', calculatePosition);
    return () => window.removeEventListener('resize', calculatePosition);
  }, [isOpen]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllRead = () => {
    setNotifications((prev) =>
      prev.map((n) => ({ ...n, read: true }))
    );
  };

  const formatTime = (date: Date): string => {
    const now = new Date();
    const diff = (now.getTime() - date.getTime()) / 1000;
    if (diff < 60) return t('notifications.just_now', 'Just now');
    if (diff < 3600) return t('notifications.minutes_ago', '{{minutes}}m ago', { minutes: Math.floor(diff / 60) });
    if (diff < 86400) return t('notifications.hours_ago', '{{hours}}h ago', { hours: Math.floor(diff / 3600) });
    return date.toLocaleDateString();
  };

  const getIconClass = (type: string): string => {
    switch (type) {
      case 'success':
        return 'bi-check-circle-fill text-success';
      case 'warning':
        return 'bi-exclamation-triangle-fill text-warning';
      case 'error':
        return 'bi-x-circle-fill text-danger';
      default:
        return 'bi-info-circle-fill text-info';
    }
  };

  const toggleDropdown = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <div ref={dropdownRef} className="dropdown notification-dropdown">
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-link nav-link position-relative p-2"
        onClick={toggleDropdown}
        aria-label={t('notifications.unread_count', 'Notifications ({{count}} unread)', { count: unreadCount })}
        aria-expanded={isOpen}
      >
        <i className="bi bi-bell fs-5" aria-hidden="true"></i>
        {unreadCount > 0 && (
          <span className="position-absolute top-0 end-0 badge rounded-pill bg-danger">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          className={`dropdown-menu show ${isNearEdge ? 'dropdown-menu-edge-fixed' : 'dropdown-menu-end'}`}
          style={{
            width: '360px',
            maxHeight: '450px',
            overflow: 'hidden',
            position: 'fixed',
            top: '57px',
            ...(dropdownPosition || {}),
          }}
          aria-label={t('notifications.menu_label', 'Notifications menu')}
        >
          <div className="dropdown-header d-flex justify-content-between align-items-center px-3 py-2">
            <span className="fw-semibold">{t('notifications.title', 'Notifications')}</span>
            {unreadCount > 0 && (
              <span className="badge bg-secondary">{t('notifications.unread_badge', '{{count}} unread', { count: unreadCount })}</span>
            )}
          </div>
          <div className="dropdown-divider"></div>

          <div
            className="notifications-list"
            style={{ maxHeight: '300px', overflowY: 'auto' }}
          >
            {notifications.length === 0 ? (
              <div className="text-center py-4 text-muted">
                <i className="bi bi-bell-slash fs-4 mb-2 d-block"></i>
                <p className="mb-0 small">{t('notifications.empty', 'No notifications')}</p>
              </div>
            ) : (
              <>
                {notifications.slice(0, 5).map((notification) => (
                  <React.Fragment key={notification.id}>
                    <button
                      type="button"
                      className="dropdown-item p-3"
                      onClick={() => markAsRead(notification.id)}
                      style={{
                        backgroundColor: notification.read
                          ? 'transparent'
                          : 'rgba(var(--bs-primary-rgb), 0.05)',
                        border: 'none',
                        width: '100%',
                        textAlign: 'left',
                      }}
                      aria-label={`${notification.title}: ${notification.message}`}
                    >
                      <div className="d-flex gap-3">
                        <div className="flex-shrink-0">
                          <i className={`bi ${getIconClass(notification.type)} fs-5`} aria-hidden="true"></i>
                        </div>
                        <div className="flex-grow-1 min-w-0">
                          <div className="d-flex justify-content-between align-items-start">
                            <p className="mb-0 fw-medium text-truncate pe-2">
                              {notification.title}
                            </p>
                            <span className="text-muted small flex-shrink-0">
                              {formatTime(notification.timestamp)}
                            </span>
                          </div>
                          <p className="mb-0 small text-muted text-truncate">
                            {notification.message}
                          </p>
                        </div>
                        {!notification.read && (
                          <span
                            className="flex-shrink-0 bg-primary rounded-circle"
                            style={{ width: '8px', height: '8px' }}
                            aria-hidden="true"
                          ></span>
                        )}
                      </div>
                    </button>
                    <div className="dropdown-divider"></div>
                  </React.Fragment>
                ))}
              </>
            )}
          </div>

          <div className="dropdown-footer p-2">
            {unreadCount > 0 && (
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none w-100 mb-2"
                onClick={markAllRead}
                              >
                {t('notifications.mark_all_read', 'Mark all as read')}
              </button>
            )}
            <Link
              to="/admin/notifications"
              className="btn btn-sm btn-outline-primary w-100"
              onClick={() => setIsOpen(false)}
                          >
              {t('notifications.view_all', 'View All Notifications')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
